import React, { useState, useMemo, useCallback } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import HoverZoomImage from './HoverZoomImage';
import Alert from '@amzn/meridian/alert';
import Checkbox from '@amzn/meridian/checkbox';
import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { INDEFINITE_TIME } from '@constants/strings';
import useTableSort from 'src/hooks/useTableSort';
import usePagination from 'src/hooks/usePagination';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';

import { GetItemEntriesQuery } from '@graphql/generated/graphql';
import TablePagination from './TablePagination';
import TableSearchBar from './TableSearchBar';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import './ScrollableTable.css';

// Extract the assortment entry type from the query response
type assortmentEntry = NonNullable<GetItemEntriesQuery['getItemEntries']['items']>[number];

export interface ItemAssortmentTableProps {
  assortmentData: assortmentEntry[] | null | undefined;
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  subcategories: storeSubcategory[];
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Header label for the scope column (e.g. "Subcategory" for WFM). Only
   *  rendered when `showScopeColumn` is true. */
  scopeColumnLabel?: string;
  /** Whether to render the scope (subcategory) column. GFC Drop Zone is a
   *  single fixed scope per view, so the column is omitted there. */
  showScopeColumn?: boolean;
}

interface TableRowData {
  id: string;
  itemId: string;
  title: string;
  image: string | null;
  startTime: string;
  endTime: string;
  scope: string;
}

const ItemAssortmentTable: React.FC<ItemAssortmentTableProps> = ({
  assortmentData,
  isLoading,
  hasNextPage,
  fetchNextPage,
  subcategories,
  selectedIds,
  onSelectionChange,
  scopeColumnLabel = 'Subcategory',
  showScopeColumn = true,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const { sortColumn, sortDirection, onSort, sortData } = useTableSort<TableRowData>('itemId');
  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;

  const rows: TableRowData[] = useMemo(() => {
    const subcategoryMap = new Map(subcategories.map((s) => [s.subcategoryId, s.subcategoryName]));
    return (assortmentData ?? []).map((entry) => ({
      id: entry.entryId,
      itemId: entry.itemId,
      title: entry.itemMetaDataEntry?.title ?? 'Unknown',
      image: entry.itemMetaDataEntry?.image ?? null,
      startTime: getDateInUTC(entry.startTime),
      endTime: entry.endTime >= INDEFINITE_TIME ? 'Indefinite' : getDateInUTC(entry.endTime),
      scope: entry.subcategoryId ? (subcategoryMap.get(entry.subcategoryId) ?? '') : '',
    }));
  }, [assortmentData, subcategories]);

  const filteredRows = useMemo(() => {
    if (!searchValue) return sortData(rows);
    const query = searchValue.toLowerCase();
    return sortData(
      rows.filter((r) => r.itemId.toLowerCase().includes(query) || r.title.toLowerCase().includes(query)),
    );
  }, [rows, searchValue, sortData]);

  const pagination = usePagination({ totalItems: filteredRows.length });
  const paginatedRows = useMemo(
    () => filteredRows.slice(pagination.visibleIndices[0], pagination.visibleIndices[1]),
    [filteredRows, pagination.visibleIndices],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      const next = checked ? new Set(filteredRows.map((r) => r.id)) : new Set<string>();
      onSelectionChange(next);
    },
    [filteredRows, onSelectionChange],
  );

  const handleRowSelect = useCallback(
    (id: string, checked: boolean) => {
      const next = new Set(selectedIds);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange],
  );

  const totalSelectedCount = filteredRows.filter((r) => selectedIds.has(r.id)).length;
  const allFilteredSelected = filteredRows.length > 0 && totalSelectedCount === filteredRows.length;
  const someFilteredSelected = totalSelectedCount > 0 && !allFilteredSelected;

  if (isLoading) {
    return (
      <Column alignmentHorizontal="center" spacingInset="500">
        <Loader type="circular" size="large" />
      </Column>
    );
  }

  if (!assortmentData || assortmentData.length === 0) {
    return (
      <Column alignmentHorizontal="center" spacingInset="500">
        <Text type="b300">No results found.</Text>
      </Column>
    );
  }

  return (
    <Column>
      <TableSearchBar
        value={searchValue}
        onChange={setSearchValue}
        placeholder={`Search by ${itemIdentifierLabel} or title...`}
      />
      <div className="scrollable-table-container">
        <Table
          headerRows={1}
          showDividers
          spacing="small"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableRow backgroundColor="secondary">
            <TableCell>
              <Checkbox
                checked={someFilteredSelected ? 'mixed' : allFilteredSelected}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </TableCell>
            <TableCell sortColumn="itemId">{itemIdentifierLabel}</TableCell>
            <TableCell>Image</TableCell>
            <TableCell sortColumn="title">Title</TableCell>
            {showScopeColumn && <TableCell sortColumn="scope">{scopeColumnLabel}</TableCell>}
            <TableCell sortColumn="startTime">Start Date</TableCell>
            <TableCell sortColumn="endTime">End Date</TableCell>
          </TableRow>
          {paginatedRows.length > 0 ? (
            paginatedRows.map((row) => (
              <TableRow key={row.id} highlightOnHover>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(row.id)}
                    onChange={(checked: boolean) => handleRowSelect(row.id, checked)}
                    aria-label={`Select ${row.itemId}`}
                  />
                </TableCell>
                <TableCell>{row.itemId}</TableCell>
                <TableCell>
                  {row.image ? (
                    <HoverZoomImage src={row.image} alt={row.itemId} width={32} height={32} />
                  ) : (
                    <Text type="b200">No image</Text>
                  )}
                </TableCell>
                <TableCell>{row.title}</TableCell>
                {showScopeColumn && <TableCell>{row.scope}</TableCell>}
                <TableCell>{row.startTime}</TableCell>
                <TableCell>{row.endTime}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell columnSpan={showScopeColumn ? 7 : 6} alignmentHorizontal="center">
                <Alert size="large">No products match</Alert>
              </TableCell>
            </TableRow>
          )}
        </Table>
      </div>

      <TablePagination pagination={pagination} totalItems={filteredRows.length} />

      {hasNextPage && (
        <Row alignmentHorizontal="center" spacingInset="400">
          <Button
            onClick={async () => {
              await fetchNextPage();
            }}
            type="primary"
          >
            Load More
          </Button>
        </Row>
      )}
    </Column>
  );
};

export default ItemAssortmentTable;
